import React, { useState } from 'react';
import { graphql, PageProps } from 'gatsby';

import Layout from 'elements/layout/layout';
import Heading from 'elements/typography/heading';
import {
  AllPlansSubtitleWrapper,
  AllPlansTitleWrapper,
  CompareLinkWrapper,
  PricingBannersContainer,
  ProCTASectionWrapper,
  TitleBanner,
  TitleWrapper,
  ToggleWrapper,
  ConfettiSection,
  PricingContainer,
  CallToActionSection
} from 'src/styles/pages/pricing-page.styles';
import ToggleInput from 'elements/inputs/ToggleInput';
import { PricingDisplayMode } from 'elements/pricing/PricingBanner';
import Paragraph from 'elements/typography/paragraph';
import LinkSimple from 'elements/links/Link.simple';
import ProPlanCTA from 'elements/pricing/ProPlanCTA';
import FAQSection from 'organisms/faq/FAQSection';
import CallToActionSecondary from 'src/molecules/callToAction/CallToAction.secondary';
import PricingPlanBanners from 'organisms/pricing/PricingPlanBanners';
import { HeadingContentfulModel } from 'src/api/models/Heading';
import {
  BoxCTAContentfulModel,
  PricingBannersSectionContentfulModel,
  PricingContentfulModel,
  PricingSectionContentfulModel
} from 'src/api/models/Pricing';
import LinkPro from 'elements/links/Link.pro';
import { FaqContainerContentfulModel } from 'src/api/models/Faq';
import PromotionModal from 'src/organisms/promotion/PromotionModal';

interface PricingPageProps extends PageProps {
  data: {
    contentfulPage: {
      title: string;
      subtitle: string;
      seoTitle: string;
      seoDescription?: string;
      displayPromotionModal?: boolean;
      seoImage?: {
        file?: {
          url: string;
        }
      }
      contentfulchildren: (PricingBannersSectionContentfulModel | PricingSectionContentfulModel)[];
    }
  };
}

const pricingToggleOptions = [
  {
    label: 'Annual',
    value: 'annual'
  },
  {
    label: 'Monthly',
    value: 'monthly'
  }
];

const PricingPage = (props: PricingPageProps): JSX.Element => {
  const [ activePriceMode, setActivePriceMode ] = useState<PricingDisplayMode>('annual');

  const helmetData = {
    title: props.data.contentfulPage.seoTitle,
    description: props.data.contentfulPage.seoDescription,
    previewImageURL: props.data.contentfulPage.seoImage?.file?.url
  }
  const displayModal = props.data.contentfulPage?.displayPromotionModal;

  const { contentfulPage } = props.data;
  const { title, subtitle } = contentfulPage;

  const pricingPlansSectionData = contentfulPage.contentfulchildren.find(d => d.label === 'Pricing Plans Container').contentfulchildren;
  const pricingPlans = (pricingPlansSectionData
    .find(child => child.internal?.type === 'ContentfulPricing') as PricingContentfulModel)
    .pricingItems;

  const ctaContainerSection = contentfulPage.contentfulchildren.find(child => child.label === 'CTA Container').contentfulchildren;
  const ctaContainerHeadingText = (ctaContainerSection
    .find(child => child.internal?.type === 'ContentfulHeading') as HeadingContentfulModel)
    .text;
  const boxCtaSection = (ctaContainerSection
    .find(child => child.internal?.type === 'ContentfulBoxCta') as BoxCTAContentfulModel);

  const faqSection = contentfulPage.contentfulchildren.find(child => child.label === 'FAQ Container').contentfulchildren;
  const faqSectionTitle = (faqSection.find(child => child.internal?.type === 'ContentfulHeading') as HeadingContentfulModel).text;
  const faqItems = (faqSection.find(child => child.internal?.type === 'ContentfulFaq') as FaqContainerContentfulModel)
    .faqItems
    .map(faqItem => ({
      question: faqItem.question.question,
      answer: faqItem.answer.raw
    }));

  return (
    <Layout helmet={helmetData} currentPageId="zMdWLDPfmvofGncLpQPue" location={props.location}>
      <ConfettiSection>
        <PricingContainer>
          <TitleBanner>
            <TitleWrapper>
              <Heading text={title} size="54px" sizeMd="32px" align="center" />
            </TitleWrapper>

            <Heading text={subtitle} size="24px" weight="light" align="center" level={2} fontFamily="Lato" />
          </TitleBanner>

          <PricingBannersContainer>
            <PricingPlanBanners pricingPlans={pricingPlans} pricingDisplayMode={activePriceMode} />
          </PricingBannersContainer>

          <ToggleWrapper>
            <ToggleInput options={pricingToggleOptions}
                         onChange={(newValue) => setActivePriceMode(newValue as PricingDisplayMode)} />

            <Paragraph text="2 months free with annual purchase" size="14px" align="center" weight="light" />
          </ToggleWrapper>
        </PricingContainer>
      </ConfettiSection>

      <AllPlansTitleWrapper>
        <Heading text={ctaContainerHeadingText} level={2} size="40px" sizeMd="24px" align="center" />
      </AllPlansTitleWrapper>

      <AllPlansSubtitleWrapper>
        <Paragraph
          text="Your own website / Unlimited members / access to Mighty Networks mobile apps / Direct messaging  / SEO features"
          align="center"
          size="22px"
          sizeMd="15px"
          weight="light" />
      </AllPlansSubtitleWrapper>

      <CompareLinkWrapper>
        <LinkSimple text="Compare Plans" url="/pricing-plan-details" color="main" />
      </CompareLinkWrapper>

      <ProCTASectionWrapper>
        <ProPlanCTA description={boxCtaSection.description.description}
          highlightImageUrl={boxCtaSection.highlightImage.file.url}
          logoUrl={boxCtaSection.logo.file.url}
          floatingShapeText={boxCtaSection.shapeContent.shapeContent}
          cta={<LinkPro url="/request-a-call-form" text={boxCtaSection.cta.text} />} />
      </ProCTASectionWrapper>

      <FAQSection title={faqSectionTitle} faqItems={faqItems} />

      <CallToActionSection>
        <CallToActionSecondary title="Have more questions?">
          <Paragraph align="center" size="22px" sizeSubLg="14px" sizeLg="22px" weight="light">Compare pricing plans <a href="/pricing-plan-details">here</a> or <a
            href="https://faq.mightynetworks.com/hc/en-us/requests/new">drop us a line</a>.</Paragraph>
        </CallToActionSecondary>
      </CallToActionSection>

      <PromotionModal displayModal={displayModal}/>
    </Layout>
  );
};

// TODO: Arrange graphql into graph blocks and exports under src/api folder
export const query = graphql`
  query PricingPageQuery {
    contentfulPage(contentful_id: {eq: "zMdWLDPfmvofGncLpQPue"}) {
      title
      subtitle
      seoTitle
      seoDescription
      seoImage {
        file {
          url
        }
      }
      displayPromotionModal
      contentfulchildren {
        ... on ContentfulSection {
          id
          label
          contentfulchildren {
            ... on ContentfulPricing {
              internal {
                type
              }
              pricingItems {
                id
                title
                subtitle
                description {
                  description
                }
                annualPrice
                monthlyPriceDescription
                monthlyPrice
                annualPriceDescription
                featured
                cta {
                  ...ButtonContent
                }
                featuresTitle
                featuresList
                disclaimer {
                  disclaimer
                }
              }
            }
            ... on ContentfulHeading {
              internal {
                type
              }
              text
            }
            ... on ContentfulBoxCta {
              internal {
                type
              }
              logo {
                file {
                  url
                }
              }
              description {
                description
              }
              cta {
                text
                url
                variant
              }
              highlightImage {
                file {
                  url
                }
              }
              shapeContent {
                shapeContent
              }
            }
          }
        }
        ... on ContentfulSection {
          contentfulchildren {
            ... on ContentfulHeading {
              internal {
                type
              }
              text
            }
            ...FaqContent
          }
        }
      }
    }
  }
`;

export default PricingPage;
